import { type FC, type ReactNode } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import {
  CONTACT_FORM_SUBJECT_KEY,
  DirectContactFormCustomerFields,
  DirectContactFormMessageField,
  DirectContactFormPhoneNumberField,
  DirectContactFormProductCodeField,
  DirectContactFormSubjectField,
  directContactFormSubjectPolicyGetByTranslatedSubject,
  directContactFormSubjectPolicyShouldShowMessage,
} from '../../../direct-contact'
import { type DirectContactFormLoggedOutNotificationType } from '../../model/directContactFormAccessorGetLoggedOutNotification'

import { type DirectContactFormValues } from './DirectContactFormModel'
import { DirectContactFormLoggedOutNotification } from './DirectContactFormNotifications/DirectContactFormLoggedOutNotification'
import { DirectContactFormOrderNumberField } from './DirectContactFormOrderNumberField'

const renderSubjectOptionValue = (value: string): ReactNode => (
  <span className="flex w-full justify-start">{value}</span>
)

export const DirectContactFormFields: FC<{
  isCustomerDataComplete: boolean
  isLoggedIn: boolean
  loggedOutNotificationType?: DirectContactFormLoggedOutNotificationType
}> = ({ isCustomerDataComplete, isLoggedIn, loggedOutNotificationType }) => {
  const intl = useIntl()
  const { clearErrors, control, setValue } =
    useFormContext<DirectContactFormValues>()
  const subject = useWatch({ control, name: 'subject' })
  const resolvedSubject = directContactFormSubjectPolicyGetByTranslatedSubject({
    intl,
    subject,
  })
  const isDoctorOrder =
    resolvedSubject.key === CONTACT_FORM_SUBJECT_KEY.DOCTOR_ORDER
  const isCopaymentExemption =
    resolvedSubject.key === CONTACT_FORM_SUBJECT_KEY.COPAYMENT_EXEMPTION
  const isMessageVisible = directContactFormSubjectPolicyShouldShowMessage({
    isLoggedIn,
    visibility: resolvedSubject.policy.directPageMessageVisibility,
  })
  const subjectLabel = intl.formatMessage({ id: 'general.label.subject' })
  const handleSubjectChange = (): void => {
    clearErrors([
      'subject',
      'images',
      'message',
      'orderId',
      'productCode',
      'selectedProducts',
    ])
    setValue('images', [])
    setValue('orderId', '')
    setValue('productCode', '')
    setValue('selectedProducts', [])
  }

  return (
    <>
      <DirectContactFormCustomerFields
        clearErrorOnClick
        isCustomerDataComplete={isCustomerDataComplete}
      />
      <DirectContactFormPhoneNumberField clearErrorOnClick />
      <DirectContactFormSubjectField
        onSubjectChange={handleSubjectChange}
        renderOptionValue={renderSubjectOptionValue}
        selectRootAriaLabel={subjectLabel}
      />
      <DirectContactFormProductCodeField
        clearErrorOnClick
        resolvedSubject={resolvedSubject}
      />
      <DirectContactFormOrderNumberField
        isLoggedIn={isLoggedIn}
        resolvedSubject={resolvedSubject}
      />
      <DirectContactFormMessageField
        clearErrorOnClick
        isVisible={isMessageVisible}
      />
      <DirectContactFormLoggedOutNotification
        isCopaymentExemption={isCopaymentExemption}
        isDoctorOrder={isDoctorOrder}
        isLoggedIn={isLoggedIn}
        notificationType={loggedOutNotificationType}
      />
    </>
  )
}
